import React, { useContext, useEffect, useMemo } from 'react';
import {
  Form,
  Row,
  Col,
  FormInstance,
  Divider,
  Select,
  Switch,
  Button,
  Tooltip,
} from 'antd';
import { useTranslation } from 'react-i18next';
import SessionContext from '@providers/Session/SessionContext';
import SelectMotLoraContracts from '@components/SelectMotLoraContracts';
import useSwr from '@hooks/useSwr';
import KeycloakContext from '@providers/Keycloak/KeycloakContext';
import { InfoCircleOutlined } from '@ant-design/icons';
import UploadDevices from './UploadDevices';

type ContractProps = {
  id: string;
  planName: string;
  assetNumber: string;
  nickname: string;
};

type DeviceProfilesProps = {
  id: string;
  name: string;
};

type DeviceConnectionsProps = {
  id: string;
  description: string;
};

const MAX_CONNECTIONS_PER_DEVICE = 4;

export interface ConfigStepDataProps {
  activation?: 'OTAA' | 'ABP';
  encryption?: 'NS' | 'APP';
  contractId?: string;
  deviceProfileId?: string;
  isUpload?: boolean;
  customerId?: string;
  contractMotId?: string;
  contractLorawanId?: string;
  requestId?: number;
  connections?: string[];
  suspended?: boolean;
  uploadedDevices?: { [key: string]: string }[];
}

interface ConfigStepProps {
  form: FormInstance<ConfigStepDataProps>;
  renderItemLabel: (label: string) => JSX.Element;
  renderRadioItem: (
    name: string,
    options: string[],
    hideLabel?: boolean,
  ) => JSX.Element;
  isBatch: boolean;
  onUpload: (values: { [key: string]: string }[] | undefined) => void;
}

const ConfigStep = ({
  form,
  renderItemLabel,
  renderRadioItem,
  isBatch,
  onUpload,
}: ConfigStepProps) => {
  const { t } = useTranslation('lorawan');
  const { isMot } = useContext(SessionContext);
  const { keycloak } = useContext(KeycloakContext);
  const customerId = keycloak?.idTokenParsed?.customerId;

  const formValues = Form.useWatch([], form);
  const selectedConnections = Form.useWatch('connections', form);
  const isActivationAbp = formValues?.activation === 'ABP';

  const { data: dataContracts } = useSwr<ContractProps[]>(
    customerId ? '/service-proxy/contracts' : null,
    {
      params: {
        customerId,
        product: 'LORAWAN',
      },
    },
  );

  const contractOptions = useMemo(
    () =>
      Array.isArray(dataContracts)
        ? dataContracts?.map(({ id, planName, assetNumber, nickname }) => ({
            label: `${assetNumber}${
              nickname ? ` - ${nickname}` : ''
            } - ${planName}`,
            value: id,
          }))
        : [],
    [dataContracts],
  );

  const { data: deviceProfiles } = useSwr<DeviceProfilesProps[]>(
    '/lorawan/device-profiles/simple',
  );

  const deviceProfilesOptions = useMemo(
    () =>
      Array.isArray(deviceProfiles)
        ? deviceProfiles?.map(({ id, name }) => ({
            label: name,
            value: id,
          }))
        : [],
    [deviceProfiles],
  );

  const { data: deviceConnections } = useSwr<DeviceConnectionsProps[]>(
    '/lorawan/connections/simple',
  );

  const hasReachedConnectionsLimit =
    (selectedConnections?.length ?? 0) >= MAX_CONNECTIONS_PER_DEVICE;

  const connectionsOptions = useMemo(
    () =>
      Array.isArray(deviceConnections)
        ? deviceConnections?.map(({ id, description }) => ({
            label: description,
            value: id,
            disabled:
              hasReachedConnectionsLimit && !selectedConnections?.includes(id),
          }))
        : [],
    [deviceConnections, hasReachedConnectionsLimit, selectedConnections],
  );

  useEffect(() => {
    if (contractOptions?.length === 1) {
      form.setFieldValue('contractId', contractOptions[0].value);
    }
  }, [contractOptions, form]);

  useEffect(() => {
    if (deviceProfilesOptions?.length === 1) {
      form.setFieldValue('deviceProfileId', deviceProfilesOptions[0].value);
    }
  }, [deviceProfilesOptions, form]);

  return (
    <Form
      colon={false}
      form={form}
      labelCol={{ span: 24 }}
      wrapperCol={{ span: 24 }}
      scrollToFirstError
      initialValues={{ activation: 'OTAA', encryption: 'NS' }}
      layout="vertical"
    >
      <Row
        gutter={[8, 8]}
        justify="center"
        style={{ marginTop: isBatch ? 24 : 0 }}
      >
        <Col xs={24} sm={8}>
          {renderRadioItem('activation', ['OTAA', 'ABP'])}
        </Col>

        {isActivationAbp && (
          <Col xs={24} sm={8}>
            {renderRadioItem('encryption', ['NS', 'APP'])}
          </Col>
        )}
      </Row>
      <Divider />
      <Row gutter={[8, 8]}>
        {isMot ? (
          <SelectMotLoraContracts form={form} span={24} />
        ) : (
          <Col span={24}>
            <Form.Item
              label={t('contract')}
              name="contractId"
              rules={[
                {
                  required: true,
                  message: t('fieldIsRequired'),
                },
              ]}
            >
              <Select
                optionFilterProp="label"
                options={contractOptions}
                loading={!contractOptions}
                disabled={!contractOptions || contractOptions?.length === 1}
                showSearch
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
        )}
      </Row>
      <Row gutter={[8, 8]}>
        {isBatch ? (
          <Col span={24} style={{ marginTop: 16 }}>
            <Form.Item
              name="uploadedDevices"
              rules={[
                {
                  required: true,
                  message: t('fieldIsRequired'),
                },
              ]}
            >
              <UploadDevices
                activation={formValues?.activation}
                encryption={formValues?.encryption}
                onUpload={values => {
                  onUpload(values);
                  form.setFieldValue('uploadedDevices', values);
                }}
              />
            </Form.Item>
          </Col>
        ) : (
          <>
            <Col span={24}>
              <Form.Item
                label={t('deviceProfile')}
                name="deviceProfileId"
                rules={[
                  {
                    required: true,
                    message: t('fieldIsRequired'),
                  },
                ]}
              >
                <Select
                  optionFilterProp="label"
                  options={deviceProfilesOptions}
                  loading={!deviceProfilesOptions}
                  disabled={
                    !deviceProfilesOptions ||
                    deviceProfilesOptions?.length === 1
                  }
                  showSearch
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label={t('connections')} name="connections">
                <Select
                  mode="multiple"
                  placeholder={t('selectUpTo', {
                    count: MAX_CONNECTIONS_PER_DEVICE,
                  })}
                  options={connectionsOptions}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                label={
                  <>
                    {renderItemLabel('suspension')}
                    <Tooltip title={t('suspensionHelp')}>
                      <Button
                        type="link"
                        size="small"
                        icon={<InfoCircleOutlined />}
                      />
                    </Tooltip>
                  </>
                }
                name="suspended"
                valuePropName="checked"
              >
                <Switch
                  checkedChildren={t('activated')}
                  unCheckedChildren={t('deactivated')}
                />
              </Form.Item>
            </Col>
          </>
        )}
      </Row>
    </Form>
  );
};

export default ConfigStep;
